import {
  Button,
  chakra,
  Divider,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { CloseIcon } from '@chakra-ui/icons'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { Location } from '../../../types/location'
import { AustralianState } from '../../../types'
import {
  INVALID_EMAIL_MESSAGE,
  isValidEmailFormat,
} from '../../../utils/validation-helpers/emailFormatValidate'
import {
  INVALID_PHONE_MESSAGE,
  isPhoneNumberValid,
} from '../../../utils/validation-helpers/mobileNumberFormatValidate'
import { validateHumanName } from '../../../utils/validation-helpers/Common'
import { postcodeValidator } from '../../../features/patient/utils/patientFieldsValidator'
import AddressSuggestionInput from '../../../components/form/AddressSuggestionInput'
import { LocationFormField } from './LocationFormField'

const AddressSuggestion = chakra(AddressSuggestionInput)

type LocationFormValues = {
  name: string
  address1: string
  address2: string
  suburb: string
  state: string
  postcode: string
  phoneNumber: string
  email: string
}

interface LocationModalProps {
  isOpen: boolean
  mode: 'add' | 'edit'
  location?: Location | null
  clinicPhone?: string
  clinicEmail?: string
  isSaving?: boolean
  onClose: () => void
  onSave: (values: LocationFormValues) => void
}

const EMPTY_FORM_VALUES: LocationFormValues = {
  name: '',
  address1: '',
  address2: '',
  suburb: '',
  state: '',
  postcode: '',
  phoneNumber: '',
  email: '',
}

const VALIDATION_MESSAGES = {
  nameRequired: 'Location name is required',
  address1Required: 'Address line 1 is required',
  suburbRequired: 'Suburb cannot be empty',
  stateRequired: 'State cannot be empty',
  postcodeRequired: 'Postcode cannot be empty',
  phoneRequired: 'Phone is required',
  emailRequired: 'Email is required',
  invalidPhone: INVALID_PHONE_MESSAGE,
  invalidEmail: INVALID_EMAIL_MESSAGE,
} as const

const ADDRESS_FIELD_MAPPING = {
  address: 'address1',
  postcode: 'postcode',
  state: 'state',
  suburb: 'suburb',
} as const

const toFormValues = (
  location: Location | null | undefined,
  clinicPhone: string | undefined,
  clinicEmail: string | undefined
): LocationFormValues => {
  if (location) {
    return {
      name: location.name,
      address1: location.address1 ?? '',
      address2: location.address2 ?? '',
      suburb: location.suburb ?? '',
      state: location.state ?? '',
      postcode: location.postcode ?? '',
      phoneNumber: location.phoneNumber ?? '',
      email: location.email ?? '',
    }
  }

  return {
    ...EMPTY_FORM_VALUES,
    phoneNumber: clinicPhone?.trim() ?? '',
    email: clinicEmail?.trim() ?? '',
  }
}

const hasExistingAddress = (location?: Location | null): boolean =>
  Boolean(location?.address1 && location?.suburb && location?.postcode && location?.state)

const trimFormValues = (data: LocationFormValues): LocationFormValues => ({
  name: data.name.trim(),
  address1: data.address1.trim(),
  address2: data.address2.trim(),
  suburb: data.suburb.trim(),
  state: data.state.trim(),
  postcode: data.postcode.trim(),
  phoneNumber: data.phoneNumber.trim(),
  email: data.email.trim(),
})

export const LocationModal = ({
  isOpen,
  mode,
  location,
  clinicPhone,
  clinicEmail,
  isSaving = false,
  onClose,
  onSave,
}: LocationModalProps) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LocationFormValues>({
    mode: 'onBlur',
    defaultValues: EMPTY_FORM_VALUES,
  })

  const [isShowAddressSuggestion, setIsShowAddressSuggestion] = useState(true)

  const addressLine1Registration = register('address1', {
    required: VALIDATION_MESSAGES.address1Required,
  })
  const suburbRegistration = register('suburb', {
    required: VALIDATION_MESSAGES.suburbRequired,
    validate: (value) => {
      if (!value || value.trim() === '') return VALIDATION_MESSAGES.suburbRequired
      const result = validateHumanName(value)
      return result.isValid || result.displayValue
    },
  })
  const stateRegistration = register('state', {
    required: VALIDATION_MESSAGES.stateRequired,
    validate: (value) => {
      if (!value || value.trim() === '') return VALIDATION_MESSAGES.stateRequired
      return (
        Object.values(AustralianState).includes(value as AustralianState) ||
        'Please select a valid State'
      )
    },
  })
  const postCodeRegistration = register('postcode', {
    required: VALIDATION_MESSAGES.postcodeRequired,
    validate: (value) => {
      if (!value || value.trim() === '') return VALIDATION_MESSAGES.postcodeRequired
      return postcodeValidator(value)
    },
  })

  const phoneRegistration = register('phoneNumber', {
    required: VALIDATION_MESSAGES.phoneRequired,
    validate: (value) => {
      const trimmed = value?.trim() ?? ''
      if (!trimmed) return VALIDATION_MESSAGES.phoneRequired
      return isPhoneNumberValid(trimmed) || VALIDATION_MESSAGES.invalidPhone
    },
  })
  const emailRegistration = register('email', {
    required: VALIDATION_MESSAGES.emailRequired,
    validate: (value) => {
      const trimmed = value?.trim() ?? ''
      if (!trimmed) return VALIDATION_MESSAGES.emailRequired
      return isValidEmailFormat(trimmed) || VALIDATION_MESSAGES.invalidEmail
    },
  })

  const phoneValue = watch('phoneNumber')
  const emailValue = watch('email')

  useEffect(() => {
    if (isOpen) {
      reset(toFormValues(location, clinicPhone, clinicEmail))
      setIsShowAddressSuggestion(!hasExistingAddress(location))
    }
  }, [isOpen, location, clinicPhone, clinicEmail, reset])

  const title = mode === 'add' ? 'Add location' : 'Edit location'
  const submitLabel = mode === 'add' ? 'Save' : 'Save changes'

  const hasAddressError = errors.address1 || errors.suburb || errors.state || errors.postcode

  const onSubmit = (data: LocationFormValues) => {
    onSave(trimFormValues(data))
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent as="form" borderRadius="xl" noValidate onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader fontSize="28px" lineHeight="1.2" fontWeight="semibold" color="gray.900" pb={4}>
          {title}
        </ModalHeader>
        <ModalCloseButton
          aria-label={`Close ${title}`}
          size="md"
          top={4}
          right={4}
          border="1px"
          borderColor="blue.100"
          color="blue.300"
          borderRadius="full"
        />
        <Divider borderColor="gray.100" />

        <ModalBody pt={4} pb={5}>
          <VStack spacing={4} align="stretch">
            <LocationFormField
              label="Location name"
              isRequired
              placeholder="e.g. Main Clinic"
              error={errors.name?.message}
              registration={register('name', {
                required: VALIDATION_MESSAGES.nameRequired,
              })}
            />

            <AddressSuggestion
              setValue={setValue}
              fieldMapping={ADDRESS_FIELD_MAPPING}
              setIsShowAddressSuggestion={setIsShowAddressSuggestion}
              {...(!isShowAddressSuggestion && { display: 'none' })}
            />
            {isShowAddressSuggestion && hasAddressError && (
              <Text fontSize="sm" fontWeight="medium" color="red.600">
                Please finish filling in the address fields
              </Text>
            )}

            {!isShowAddressSuggestion && (
              <VStack spacing={4} align="stretch">
                <Flex justifyContent="space-between" align="center">
                  <Text fontWeight="semibold" fontSize="sm" color="gray.900">
                    Address
                  </Text>
                  <Button
                    variant="link"
                    color="blue.500"
                    onClick={() => setIsShowAddressSuggestion(true)}
                    size="sm"
                  >
                    Back to address search
                  </Button>
                </Flex>
                <LocationFormField
                  label="Address line 1"
                  isRequired
                  placeholder="Street number and name"
                  error={errors.address1?.message}
                  registration={addressLine1Registration}
                />
                <LocationFormField
                  label="Address line 2 (optional)"
                  placeholder="Apartment, suite, unit, building"
                  registration={register('address2')}
                />

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  <LocationFormField
                    label="Suburb"
                    isRequired
                    error={errors.suburb?.message}
                    registration={suburbRegistration}
                  />
                  <LocationFormField
                    label="State"
                    isRequired
                    error={errors.state?.message}
                    registration={stateRegistration}
                  >
                    <Select height="42px" borderRadius="md" {...stateRegistration}>
                      <option value="">Select state</option>
                      {Object.values(AustralianState).map((state) => (
                        <option value={state} key={state}>
                          {state}
                        </option>
                      ))}
                    </Select>
                  </LocationFormField>
                  <LocationFormField
                    label="Postcode"
                    isRequired
                    error={errors.postcode?.message}
                    registration={postCodeRegistration}
                  />
                </SimpleGrid>
              </VStack>
            )}

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <LocationFormField
                label="Phone"
                isRequired
                type="tel"
                error={errors.phoneNumber?.message}
                registration={phoneRegistration}
                placeholder="eg. 0411222333"
              >
                <InputGroup>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    placeholder="eg. 0411222333"
                    {...phoneRegistration}
                  />
                  {phoneValue ? (
                    <InputRightElement>
                      <IconButton
                        aria-label="Clear phone"
                        icon={<CloseIcon boxSize={2.5} />}
                        size="xs"
                        variant="ghost"
                        isRound
                        onClick={() =>
                          setValue('phoneNumber', '', { shouldValidate: true, shouldDirty: true })
                        }
                        color="gray.400"
                        _hover={{ bg: 'gray.100', color: 'gray.600' }}
                      />
                    </InputRightElement>
                  ) : null}
                </InputGroup>
              </LocationFormField>
              <LocationFormField
                label="Email"
                isRequired
                type="email"
                error={errors.email?.message}
                registration={emailRegistration}
                placeholder="eg. example@email.com"
              >
                <InputGroup>
                  <Input
                    id="email"
                    type="email"
                    placeholder="eg. example@email.com"
                    {...emailRegistration}
                  />
                  {emailValue ? (
                    <InputRightElement>
                      <IconButton
                        aria-label="Clear email"
                        icon={<CloseIcon boxSize={2.5} />}
                        size="xs"
                        variant="ghost"
                        isRound
                        onClick={() =>
                          setValue('email', '', { shouldValidate: true, shouldDirty: true })
                        }
                        color="gray.400"
                        _hover={{ bg: 'gray.100', color: 'gray.600' }}
                      />
                    </InputRightElement>
                  ) : null}
                </InputGroup>
              </LocationFormField>
            </SimpleGrid>
          </VStack>
        </ModalBody>

        <Divider borderColor="gray.100" />
        <ModalFooter pt={4} pb={4}>
          <HStack spacing={3} w="full" justify="flex-end">
            <Button
              variant="outline"
              colorScheme="blue"
              onClick={onClose}
              minW="96px"
              borderColor="blue.200"
            >
              Cancel
            </Button>
            <Button colorScheme="blue" type="submit" minW="110px" isLoading={isSaving}>
              {submitLabel}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export type { LocationFormValues }
